import moment from 'moment';

export interface IDateFormat {
  date: string;
  dateTime: string;
  yearMonthDate: string;
  dashSmallYearMonthSmallDate: string;
  monthDateYear: string;
  month: string;
  barChartMonth: string;
  localeMonthDate: string;
  localeMonthDateSpaceYear: string;
  dashDateMonthYear: string;
  dashMonthDateYear: string;
  dashMonthDateSmallYear: string;
  dashMonthYear: string;
  dateSpreadsheet: string;
  dotMonthDateYear: string;
  dateBatchPopup: string;
  LL: string;
  LT: string;
  day: string;
  timestamp: string;
  HoursMinutes12: string;
  datepicker: string;
}

export const DateFormat: IDateFormat = {
  date: 'MM/DD/YYYY',
  dateTime: 'MM/DD/YYYY hh:mm A',
  yearMonthDate: 'YYYY-MM-DD',
  dashSmallYearMonthSmallDate: 'yyyy-MM-dd',
  monthDateYear: 'MM/DD/YYYY',
  month: 'MMM DD, YYYY',
  barChartMonth: 'MMM DD, YY',
  localeMonthDate: 'MMM DD',
  localeMonthDateSpaceYear: 'MMM DD YYYY',
  dashDateMonthYear: 'DD-MM-YYYY',
  dashMonthDateYear: 'MM-DD-YYYY',
  dashMonthDateSmallYear: 'MM-DD-yyyy',
  dashMonthYear: 'MM-YYYY',
  dateSpreadsheet: 'M.D.YY',
  dotMonthDateYear: 'MM.DD.YYYY',
  dateBatchPopup: 'MMDDYY hh:mm',
  LL: 'LL',
  LT: 'LT',
  day: 'ddd',
  timestamp: 'x',
  HoursMinutes12: 'hh:mm A',
  datepicker: 'MMM dd, yyyy',
};

type DateInput = string | Date | moment.Moment;
type DurationUnit = moment.unitOfTime.Diff;

export interface IDateRange {
  start: Date;
  end: Date;
}

export class DateUtility {
  static formatDate = (date: DateInput, format: string = DateFormat.dateTime): string => {
    if (!date) {
      return '';
    }
    return moment(date).format(format);
  };

  static difference = (
    date1: DateInput,
    date2: DateInput,
    unit: DurationUnit = 'days',
  ): number => moment(date2).diff(date1, unit);

  static noOfDays = (date1: DateInput, date2?: DateInput): number => {
    if (!date2) {
      date2 = moment().utc();
    }
    const days = DateUtility.difference(date1, date2);
    return days;
  };

  static duration = (date1: DateInput, date2: DateInput): moment.Duration =>
    moment.duration(moment(date2).diff(date1));

  static formatDurationFromSeconds = (totalSeconds: number): string => {
    const seconds = Math.max(0, Math.ceil(totalSeconds));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    const parts: string[] = [];

    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }
    if (minutes > 0) {
      parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }
    if (remainingSeconds > 0 || parts.length === 0) {
      parts.push(`${remainingSeconds} ${remainingSeconds === 1 ? 'second' : 'seconds'}`);
    }

    return parts.join(' ');
  };

  static hourMinuteTime = (
    date1: DateInput,
    date2?: DateInput,
    detail: number = 2,
  ): string => {
    if (!date2) {
      date2 = moment().utc();
    }

    const diff = DateUtility.duration(date1, date2);
    const days = DateUtility.difference(date1, date2);
    const hours = diff.get('hours');
    const mins = diff.get('minutes');
    const seconds = diff.get('seconds');
    const temp: string[] = [];

    if (days > 0) {
      temp.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
    }
    if (hours > 0) {
      temp.push(`${hours} ${hours === 1 ? 'Hr' : 'Hrs'}`);
    }
    if (mins > 0) {
      temp.push(`${mins} ${mins === 1 ? 'Min' : 'Mins'}`);
    }
    if (seconds > 0) {
      temp.push(`${seconds < 0 ? 1 : seconds} ${seconds === 1 ? 'Sec' : 'Secs'}`);
    }

    return temp.slice(0, detail).join(' ');
  };

  static addDays = (date: DateInput, days: number): Date =>
    moment(date).add(days, 'days').toDate();

  static addMonths = (date: DateInput, months: number): Date =>
    moment(date).add(months, 'months').toDate();

  static addYears = (date: DateInput, years: number): Date =>
    moment(date).add(years, 'years').toDate();

  static toISODate = (date: DateInput): string => moment(date).toISOString();

  static isValidDate(date: unknown): boolean {
    if (!date) return false;

    try {
      if (typeof date === 'string') {
        // Ensure the string is not purely numeric
        if (!isNaN(Number(date))) return false;
      }

      if (typeof date === 'string' || date instanceof Date) {
        const parsedDate = new Date(date);
        // Check if the date is valid
        return !isNaN(parsedDate.getTime());
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  static convertDatesToISOString(obj: any): any {
    const process = (value: any): any => {
      if (this.isValidDate(value)) {
        return this.formatDate(value, 'HH:mm');
      }

      if (Array.isArray(value)) {
        return value.map(process);
      }

      if (value && typeof value === 'object') {
        Object.keys(value).forEach(key => {
          value[key] = process(value[key]);
        });
      }
      return value;
    };
    return process(obj);
  }

  static getYesterdayRange = (): IDateRange => {
    const yesterday = moment().subtract(1, 'day');
    const start = yesterday.startOf('day').toDate();
    const end = yesterday.endOf('day').toDate();
    return { start, end };
  };

  static getWeekToDateRange = (): string => {
    const startOfWeek = moment().startOf('week').add(1, 'day'); // Monday
    const today = moment();

    return `${startOfWeek.format('YYYY-MM-DD')},${today.format('YYYY-MM-DD')}`;
  };
}
